import { EditorState } from 'minidoc-editor/dist/types/types';
import { JSX } from 'preact';
import { Dispatch, StateUpdater } from 'preact/hooks';
import { SalesBlockState, CardStyles, IconStyles, SalespageContent } from 'server/types';
import type { SalesPageState } from './page-editor';

export type PageSelection = {
  id: string;
  value: EditorState['selection'];
};

export type PageStateProps = {
  pageState?: SalesPageState<SalespageContent>;
  setPageState?: Dispatch<StateUpdater<SalesPageState<SalespageContent>>>;
};

type BaseProps<T> = {
  id: string;
  class?: string;
  bgcolor?: string;
  fgcolor?: string;
  isBgLight: boolean;
  version?: number;
  state: T;
  bulletStyle?: string;
  bulletColor?: string;
};

export type ViewerProps<T> = BaseProps<T> & {
  setState?: undefined;
};

export type EditorProps<T> = BaseProps<T> & {
  isSelected: boolean;
  class?: string;
  selection?: PageSelection;
  setSelection(selection: PageSelection): void;
  setState: Dispatch<StateUpdater<T>>;
  // For block settings
  blockState?: any;
  onBlockStateChange?: (updater: (state: any) => any) => void;
  pageState?: any;
  setPageState?: any;
};

export type SalesBlockDefinition<TState = any> = {
  initialState: TState;

  /**
   * If specified, is used to set the block state when adding a new block.
   */
  initialBlockState?: SalesBlockInitialState<TState>;

  /**
   * A unique name for the sales block.
   */
  type: string;

  /**
   * The human-friendly display name.
   */
  name: string;

  /**
   * Render a miniature representation / icon of the component.
   */
  MiniView(): JSX.Element;

  /**
   * If defined, this will be called before deleting the block, so the block
   * can prompt the user, do any cleanup, etc. If it returns false, the block
   * will not be deleted. Any other return value will result in deletion.
   */
  onDelete?(props: { state: TState }): Promise<boolean | undefined>;

  /**
   * Render the block in editor mode.
   */
  Editor(props: EditorProps<TState>): JSX.Element | undefined;

  /**
   * Render the block in viewer mode.
   */
  Viewer(props: ViewerProps<TState>): JSX.Element | undefined;
};

export type SalesBlockInitialState<TState = any> = Omit<SalesBlockState<TState>, 'id' | 'state'> & {
  bulletStyle?: string;
  bulletColor?: string;
};

export type CardStyleBlockState = SalesBlockState<any> & {
  cardStyles?: CardStyles;
  iconStyles?: IconStyles;
};

export type BlockStateUpdater<T = CardStyleBlockState> = Dispatch<StateUpdater<T>>;

export type StylePanelProps = {
  onClose: () => void;
  blockState?: CardStyleBlockState;
  onBlockStateChange?: BlockStateUpdater;
};
